import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { ProfileCard } from '../components/profile-settings/ProfileCard.js';
import { StatisticsGrid } from '../components/profile-settings/StatisticsGrid.js';
import { PreferenceSwitch } from '../components/profile-settings/PreferenceSwitch.js';
import { AboutCard } from '../components/profile-settings/AboutCard.js';
import { useAudioStore } from '../store/audio.store.js';

export function ProfileSettingsScreen() {
  const { user, logout, isSessionChecked } = useAuthStore();
  const navigate = useNavigate();

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  const handleBack = () => {
    navigate('/home');
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm('Are you sure you want to log out of your session?');
    if (confirmLogout) {
      logout();
      navigate('/login');
    }
  };

  // Preference switches states from global store
  const { soundEnabled, setSoundEnabled, musicEnabled, setMusicEnabled } = useAudioStore();
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoMarkEnabled, setAutoMarkEnabled] = useState(false);

  return (
    <div className="relative flex h-screen w-screen flex-col justify-between overflow-hidden">
      {/* 3D background grids */}
      <BackgroundEffects />

      {/* Header bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full flex items-center justify-between px-6 py-4 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30"
      >
        <button
          onClick={handleBack}
          className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-wider text-surface-200 hover:text-white transition-colors cursor-pointer"
        >
          <span>&larr;</span>
          <span>Back</span>
        </button>

        <div className="flex flex-col items-center select-none">
          <h2 className="font-display text-base font-black tracking-wider text-white">
            PROFILE & SETTINGS
          </h2>
          <span className="text-[9px] font-bold tracking-widest text-gradient uppercase">
            Configure Preferences & Statistics
          </span>
        </div>

        {/* Empty status element placeholder */}
        <div className="w-[70px]" />
      </motion.header>

      {/* Main split panels dashboard */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 px-8 py-4 max-h-[calc(100vh-120px)] z-20 overflow-y-auto items-start">
        
        {/* Left Column: Profile & Preference Toggles */}
        <div className="space-y-4 h-full flex flex-col justify-between max-h-[290px] lg:max-h-full">
          {user && (
            <ProfileCard
              displayName={user.displayName}
              avatarSeed={user.displayName}
              isOnline={true}
            />
          )}

          {/* Preferences toggles list */}
          <div className="glass rounded-3xl p-4 border border-white/10 shadow-glow-lg text-left space-y-2">
            <label className="text-[9px] font-bold uppercase tracking-wider text-surface-500 block mb-1">
              Game Preferences
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <PreferenceSwitch
                label="Sound Effects"
                icon="🔊"
                isEnabled={soundEnabled}
                onToggle={() => setSoundEnabled(!soundEnabled)}
              />
              <PreferenceSwitch
                label="Music Loop"
                icon="🎵"
                isEnabled={musicEnabled}
                onToggle={() => setMusicEnabled(!musicEnabled)}
              />
              <PreferenceSwitch
                label="Vibration"
                icon="📳"
                isEnabled={vibrationEnabled}
                onToggle={() => setVibrationEnabled(!vibrationEnabled)}
              />
              <PreferenceSwitch
                label="Notifications"
                icon="🔔"
                isEnabled={notificationsEnabled}
                onToggle={() => setNotificationsEnabled(!notificationsEnabled)}
              />
              <PreferenceSwitch
                label="Auto Mark"
                icon="🎯"
                isEnabled={autoMarkEnabled}
                onToggle={() => setAutoMarkEnabled(!autoMarkEnabled)}
              />
            </div>
          </div>
        </div>

        {/* Center Column: Statistics Metrics grids */}
        <div className="h-full flex flex-col justify-center max-h-[200px] lg:max-h-full">
          <StatisticsGrid
            totalMatches={28}
            wins={8}
            winRate={29}
            bestPrize="Full House"
            playTime="04:24:12"
            rank={12}
          />
        </div>

        {/* Right Column: About details & Logout buttons */}
        <div className="space-y-4 h-full flex flex-col justify-between max-h-[290px] lg:max-h-full">
          <AboutCard version="1.0.0" />

          {/* Primary Logout action buttons */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            style={{
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.25)',
            }}
            className="w-full py-3 rounded-2xl bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-widest cursor-pointer shadow-glow"
          >
            Log Out Account
          </motion.button>
        </div>

      </main>
    </div>
  );
}
export default ProfileSettingsScreen;
